import { useEffect, useMemo, useState } from "react";
const euro = (v) => (Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const statutsActifs = ["SIGNE", "ACTIF", "ARRIVANT_ECHEANCE"];
const statutsFinaux = ["TERMINE", "RESILIE", "REFUSE"];

export default function DashboardContrats({
  clientSupabase, groupe, clubsFiltres = [], dateDebut = "", dateFin = "",
  periodeLibelle = "Toutes périodes", onOuvrir, onRenouveler, onResume
}) {
  const [data, setData] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [filtre, setFiltre] = useState("TOUS");
  const [erreur, setErreur] = useState("");
  const [signatures, setSignatures] = useState({});
  const clubIdsKey = clubsFiltres.map((club) => String(club.id || club.identifiant)).sort().join("|");
  const idsClubs = useMemo(() => new Set(clubIdsKey ? clubIdsKey.split("|") : []), [clubIdsKey]);

  useEffect(() => {
    let actif = true;
    (async () => {
      const [r, s] = await Promise.all([
        clientSupabase.rpc("contrats_crm_dashboard", { p_groupe: groupe }),
        clientSupabase.rpc("contrat_signature_statuts", { p_groupe: groupe })
      ]);
      if (!actif) return;
      if (r.error) { setErreur("Suivi contrats indisponible."); setData(null); }
      else { setData(r.data || {}); setErreur(""); }
      if (!s.error) setSignatures(Object.fromEntries((s.data || []).map((item) => [item.contrat_id, item.statut])));
    })();
    return () => { actif = false; };
  }, [clientSupabase, groupe]);

  const jours = (date) => date ? Math.ceil((new Date(`${date}T12:00:00`) - new Date()) / 86400000) : null;
  const couvrePeriode = (contrat) => {
    if (!dateDebut && !dateFin) return true;
    const debutContrat = String(contrat.date_debut || "").slice(0, 10);
    const finContrat = String(contrat.date_fin || "").slice(0, 10);
    if (!debutContrat && !finContrat) return false;
    return (!dateFin || !debutContrat || debutContrat <= dateFin) && (!dateDebut || !finContrat || finContrat >= dateDebut);
  };
  const aRenouveler = (contrat, limite) => {
    const restant = jours(contrat.date_fin);
    return restant != null && restant >= 0 && restant <= limite && !statutsFinaux.includes(contrat.statut);
  };

  const contratsDashboard = useMemo(
    () => (data?.contrats || []).filter((contrat) => idsClubs.has(String(contrat.club_id)) && couvrePeriode(contrat)),
    // La liste des IDs et l'intervalle sont l'unique population du Dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, idsClubs, dateDebut, dateFin]
  );
  const contratsActifs = contratsDashboard.filter((contrat) => statutsActifs.includes(contrat.statut));
  const contratsE12 = contratsDashboard.filter((contrat) => aRenouveler(contrat, 365));
  const contratsE6 = contratsDashboard.filter((contrat) => aRenouveler(contrat, 183));
  useEffect(() => {
    onResume?.(data ? { contrats: contratsDashboard, echeance: contratsDashboard.filter((contrat) => aRenouveler(contrat, 365)) } : null);
    // Le résumé suit la même population filtrée que le tableau des contrats.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, contratsDashboard, onResume]);
  const montant = (liste) => liste.reduce((somme, contrat) => somme + (Number(contrat.montant_annuel_ttc) || 0), 0);

  // Le prédicat aRenouveler est pur et ne dépend que du contrat et de D2.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const liste = useMemo(() => contratsDashboard.filter((contrat) => {
    if (filtre === "TOUS") return true;
    if (filtre === "ACTIFS") return statutsActifs.includes(contrat.statut);
    if (filtre === "TERMINE") return contrat.statut === "TERMINE";
    if (filtre === "RESILIE") return contrat.statut === "RESILIE";
    const limites = { E12: 365, E6: 183, E3: 92, E60: 60, E30: 30 };
    return limites[filtre] != null && aRenouveler(contrat, limites[filtre]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contratsDashboard, filtre]);

  return <section style={{ background: "white", padding: 12, borderRadius: 10, marginBottom: 16 }}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 8, marginBottom: 10 }}>{[
      ["ACTIFS", "CONTRATS ACTIFS", contratsActifs], ["E12", "À RENOUVELER ≤ 12 MOIS", contratsE12], ["E6", "À RENOUVELER ≤ 6 MOIS", contratsE6]
    ].map(([code, libelle, contrats]) => <button key={code} onClick={() => { setFiltre(code); setOuvert(true); }} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 10, background: "white", textAlign: "left" }}><small>{libelle}</small><div style={{ fontSize: 21, fontWeight: "bold" }}>{contrats.length}</div><strong>{euro(montant(contrats))} € TTC/an</strong></button>)}</div>
    <button onClick={() => setOuvert(!ouvert)} style={{ padding: "9px 14px", background: "#0d5f4d", color: "white", border: 0, borderRadius: 6, fontWeight: "bold" }}>SUIVI CONTRATS {ouvert ? "▲" : "▼"}</button>
    {ouvert && <div style={{ marginTop: 12 }}>{erreur && <p>{erreur}</p>}<strong>Période Dashboard : {periodeLibelle}</strong><div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>{[["TOUS", "Tous"], ["ACTIFS", "Actifs"], ["E12", "≤ 12 mois"], ["E6", "≤ 6 mois"], ["E3", "≤ 3 mois"], ["E60", "≤ 60 jours"], ["E30", "≤ 30 jours"], ["TERMINE", "Terminés"], ["RESILIE", "Résiliés"]].map(([v, l]) => <button key={v} onClick={() => setFiltre(v)} style={{ fontWeight: filtre === v ? "bold" : "normal" }}>{l}</button>)}</div>
      <div style={{ marginTop: 8 }}><strong>{liste.length} contrat(s) — {euro(montant(liste))} € TTC/an</strong></div>
      <div style={{ overflowX: "auto", marginTop: 10 }}><table style={{ width: "100%", borderCollapse: "collapse" }}><thead><tr>{["Club", "Référence", "Commercial", "Type", "Début", "Fin", "Durée", "TTC/an", "Statut", "Signature", "Temps restant", "Actions"].map((h) => <th key={h} style={{ padding: 6, borderBottom: "1px solid #ccc", textAlign: "left" }}>{h}</th>)}</tr></thead><tbody>{liste.map((c) => <tr key={c.id}><td>{c.club_nom}</td><td>{c.reference}</td><td>{c.commercial_nom || "—"}</td><td>{c.type_contrat}</td><td>{c.date_debut || "—"}</td><td>{c.date_fin || "—"}</td><td>{c.duree_mois ? `${c.duree_mois} mois` : "—"}</td><td>{euro(c.montant_annuel_ttc)} €</td><td>{c.statut}</td><td>{(signatures[c.id] || "NON_ENVOYE").replaceAll("_", " ")}</td><td>{jours(c.date_fin) == null ? "—" : jours(c.date_fin) < 0 ? "Échu" : `${jours(c.date_fin)} j`}</td><td><button onClick={() => onOuvrir(c)}>OUVRIR</button> <button onClick={() => onRenouveler(c)}>RENOUVELER</button></td></tr>)}</tbody></table>{liste.length === 0 && <p>Aucun contrat dans cette sélection.</p>}</div>
    </div>}
  </section>;
}
